import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ManageBehaviorStrategiesService } from '../manage-behavior-strategies.service';
import {
  BehaviorSubject,
  filter,
  finalize,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  CreateOwnStrategyPayload,
  StrategyItem,
  StrategyQueryParams,
} from '../strategy.interface';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { SvgIconComponent } from 'angular-svg-icon';
import { Router } from '@angular/router';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ManageStudentDrawerParams } from '../../../../insights.interface';
import { StudentInfoCardComponent } from '../../../../../../components/student-info-card/student-info-card.component';
import { BehaviorInfoCardComponent } from '../../../../../../components/behavior-info-card/behavior-info-card.component';

interface ManageOwnStrategyParams {
  strategy: StrategyItem;
  mode: 'view' | 'edit' | 'create';
  behaviorId?: number;
  strategyTypeId?: number;
  from?: 'Guide me' | 'Create my own';
  caseParams: ManageStudentDrawerParams;
}

@Component({
  selector: 'app-create-own-strategy',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NzInputModule,
    NzFormModule,
    NzButtonModule,
    SvgIconComponent,
    NzSpinModule,
    StudentInfoCardComponent,
    BehaviorInfoCardComponent,
  ],
  templateUrl: './create-own-strategy.component.html',
  styleUrl: './create-own-strategy.component.scss',
})
export class CreateOwnStrategyComponent implements OnDestroy {
  @Input() caseBehaviorId!: number;
  @Input() type!: number;
  @Output() cancelled = new EventEmitter<void>();

  private readonly router = inject(Router);

  strategy?: StrategyItem;
  mode: 'view' | 'edit' | 'create' = 'create';
  from!: 'Guide me' | 'Create my own' | '';
  behaviorId?: number;
  isPublished = false;
  strategyTypeId?: number;
  caseParams!: ManageStudentDrawerParams;

  loading$: Observable<boolean> = of(false);

  createOwnStrategyForm: FormGroup;

  private readonly loaderService = inject(LoaderService);
  private readonly commonService = inject(CommonService);
  private readonly drawerService = inject(DrawerService);
  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);

  private readonly paramsSubject =
    new BehaviorSubject<StrategyQueryParams | null>(null);

  readonly defaultParams$ = this.paramsSubject.asObservable();

  readonly MAX_LENGTHS = {
    title: 200,
    description: 4000,
    details: 2000,
    guideline: 500,
  };

  readonly ERROR_MESSAGES = {
    title: {
      required: 'Title is required',
      maxlength: `Title cannot exceed ${this.MAX_LENGTHS.title} characters`,
      pattern: 'Title can only contain letters and spaces',
    },
    description: {
      required: 'Description is required',
      maxlength: `Description cannot exceed ${this.MAX_LENGTHS.description} characters`,
    },
    details: {
      required: 'Details are required',
      maxlength: `Details cannot exceed ${this.MAX_LENGTHS.details} characters`,
    },
    guideline: {
      required: 'Guideline is required',
    },
  };

  constructor() {
    this.createOwnStrategyForm = this.fb.group({
      title: [
        '',
        [Validators.required, Validators.maxLength(this.MAX_LENGTHS.title)],
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.MAX_LENGTHS.description),
        ],
      ],
      guidelines: this.fb.array([this.createDefaultGuideline()]),
      details: [
        '',
        [Validators.required, Validators.maxLength(this.MAX_LENGTHS.details)],
      ],
    });
    this.setupDrawerSubscription();
    this.setupFormValidation();
    this.loading$ = this.loaderService.loading$;
  }

  private setupFormValidation(): void {
    this.createOwnStrategyForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validateForm();
      });
  }

  private validateForm(): void {
    Object.keys(this.createOwnStrategyForm.controls).forEach(key => {
      const control = this.createOwnStrategyForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.createOwnStrategyForm.get(controlName);
    if (!control || !control.errors) return '';

    const errors =
      this.ERROR_MESSAGES[controlName as keyof typeof this.ERROR_MESSAGES];
    const firstError = Object.keys(control.errors)[0];
    return errors[firstError as keyof typeof errors] || 'Invalid input';
  }

  get guidelinesArray() {
    return this.createOwnStrategyForm?.get('guidelines') as FormArray;
  }

  createGuideline(guideline?: string): FormGroup {
    return this.fb.group({
      guideline: [guideline?.trim() || '', [Validators.required]],
      sequenceNumber: [this.guidelinesArray?.controls?.length + 1 || 1],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createDefaultGuideline(): FormGroup {
    const formGroup = this.createGuideline('');
    formGroup.get('sequenceNumber')?.setValue(1);
    return formGroup;
  }

  showAddButton(index: number): boolean {
    return (
      index === this.guidelinesArray.length - 1 &&
      this.guidelinesArray.at(index).get('guideline')?.value?.trim()
    );
  }

  showDeleteButton(index: number): boolean {
    return index > 0;
  }

  addGuideline(): void {
    const lastGuidelineControl = this.guidelinesArray.at(
      this.guidelinesArray.length - 1
    );
    if (!lastGuidelineControl.get('guideline')?.value?.trim()) {
      return;
    }
    const newGuideline = this.createGuideline('');
    newGuideline
      .get('sequenceNumber')
      ?.setValue(this.guidelinesArray.length + 1);
    this.guidelinesArray.push(newGuideline);
    this.updateSequenceNumbers();
  }

  removeGuideline(index: number): void {
    if (this.showDeleteButton(index)) {
      this.guidelinesArray.removeAt(index);
      this.updateSequenceNumbers();
    }
  }

  private updateSequenceNumbers(): void {
    this.guidelinesArray.controls.forEach((control, index) => {
      control.get('sequenceNumber')?.setValue(index + 1);
    });
  }

  guidelinesRemainingChars(index: number): number {
    const control = this.guidelinesArray.at(index).get('guideline');
    const remaining = 500 - (control?.value?.length || 0);
    return Math.max(0, remaining);
  }

  saveStrategy(isPublish: boolean): void {
    this.isPublished = isPublish;
    if (this.mode === 'edit') {
      this.createModification();
    } else {
      this.createOwnStrategy();
    }
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageOwnStrategyParams>(DRAWER_IDS.CREATE_OWN_STRATEGY)
      .pipe(
        takeUntil(this.destroy$),
        filter(params => !!params?.strategy || !!params?.strategyTypeId)
      )
      .subscribe(params => {
        if (params) {
          this.handleDrawerParams(params);
        }
      });
  }

  private handleDrawerParams(params: ManageOwnStrategyParams): void {
    this.caseParams = params.caseParams;
    this.strategy = params.strategy;
    this.behaviorId = params.behaviorId;
    this.mode = params?.mode || 'edit';
    this.strategyTypeId = params.strategyTypeId || 0;
    if (params.from) {
      this.from = params.from;
    }
    if (this.mode === 'edit' && this.strategy) {
      this.populateFormForEdit();
    }
  }

  private populateFormForEdit(): void {
    this.guidelinesArray.clear();

    this.createOwnStrategyForm.patchValue({
      title: this.strategy?.title,
      description: this.strategy?.description,
      details: this.strategy?.details,
    });

    if (this.strategy?.guidelines && this.strategy.guidelines.length > 0) {
      this.strategy.guidelines.forEach(guideline => {
        this.guidelinesArray.push(
          this.createGuideline(guideline.guideline ?? '')
        );
      });
    } else {
      this.guidelinesArray.push(this.createGuideline(''));
    }

    this.updateSequenceNumbers();
  }
  private getBasePayload() {
    const formValue = this.createOwnStrategyForm.value;
    return {
      title: formValue.title ?? '',
      description: formValue.description ?? '',
      details: formValue.details ?? '',
      guidelines: this.guidelinesArray.value,
    };
  }

  createOwnStrategy(): void {
    if (this.createOwnStrategyForm.invalid) return;

    const payload: CreateOwnStrategyPayload = {
      ...this.getBasePayload(),
      caseBehaviorId: this.behaviorId ?? 0,
      strategyTypeId: this.strategyTypeId ?? 0,
      isPublish: this.isPublished,
    };

    this.submitStrategy(
      this.strategyService.createOwnStrategy(payload),
      'Behavior strategy added successfully'
    );
  }

  createModification(): void {
    if (this.createOwnStrategyForm.invalid) return;

    const payload = {
      ...this.getBasePayload(),
      caseBehaviorId: this.behaviorId ?? 0,
      behaviorStrategyId: this.strategy?.id ?? 0,
      modificationReason: 'Needs adjustment',
    };

    this.submitStrategy(
      this.strategyService.createModificationStrategy(payload),
      'Behavior strategy updated successfully'
    );
  }

  private submitStrategy(
    request: Observable<unknown>,
    successMessage: string
  ): void {
    this.loaderService.show();

    request
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();

          if (this.mode === 'edit') {
            this.strategyService
              .getBehaviorStrategyList({
                caseBehaviorId: this.behaviorId ?? 0,
                strategyType: this.strategy?.behaviourStrategyTypeId,
                pageIndex: 1,
                pageSize: 10,
              })
              .pipe(finalize(() => this.loaderService.hide()))
              .subscribe();
          }
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', successMessage);
          this.closeDrawers();
          this.router.navigate(['/insights'], {
            queryParams: { mode: 'view' },
          });
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  closeDrawers(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
    this.drawerService.closeDrawer(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
  }

  getRemainingChars(controlName: string): number {
    const control = this.createOwnStrategyForm.get(controlName);
    if (!control)
      return this.MAX_LENGTHS[controlName as keyof typeof this.MAX_LENGTHS];

    const maxLength =
      this.MAX_LENGTHS[controlName as keyof typeof this.MAX_LENGTHS];
    const currentLength = control.value?.length || 0;
    return Math.max(0, maxLength - currentLength);
  }

  getGuidelineRemainingChars(index: number): number {
    const control = this.guidelinesArray.at(index).get('guideline');
    if (!control) return this.MAX_LENGTHS.guideline;

    return Math.max(
      0,
      this.MAX_LENGTHS.guideline - (control.value?.length || 0)
    );
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.createOwnStrategyForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }
}
